import { useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState, useRef } from "react";
import styled, { css, down } from "@xstyled/styled-components";
import { useMutation } from "@apollo/client";

import { QuestionComponentProps } from "..";

import { formatUserToJobPreferencesReview } from "./utils";
import { PreferencesReviewEditModal } from "./PreferencesReviewEditModal";

import { pushAnalyticsEvent } from "@otta/analytics";
import { Spacing, Text } from "@otta/design";
import {
  UserCvParsingStatusDocument,
  UserPreferencesReviewDocument,
  CompanySizesDocument,
  UpdateUserPreferencesDocument,
} from "@otta/search/schema";
import { Spinner } from "@otta/search/components/Loading";
import { palette, pxToRem } from "@otta/design-tokens";
import { Icon, OttaIconsId } from "@otta/icons";
import { useQuery } from "@otta/search/apollo";
import { standardQuizQuestions } from "@otta/search/pages/Preferences/InitialPreferences";

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16;
  background-color: ${palette.brand.white};
  border-radius: lg;
  padding: lg;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: center;
  gap: 12;
`;

const FieldRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 0 20;
  width: 100%;

  ${down(
    "tablet",
    css`
      align-items: flex-start;
      padding: 0;
      gap: xs;
    `
  )}
`;

const FieldContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0;
  width: 100%;

  ${down(
    "tablet",
    css`
      flex-direction: column;
      align-items: flex-start;
      gap: ${pxToRem(4)};
    `
  )}
`;

const Separator = styled.div`
  width: 100%;
  height: ${pxToRem(1)};
  background: ${palette.brand.grey};
  display: none;

  ${down(
    "tablet",
    css`
      display: block;
    `
  )}
`;

const Flex = styled.div<{ gap?: string; wrap?: string }>`
  display: flex;
  flex-direction: row;
  flex-wrap: ${({ wrap }) => wrap || "nowrap"};
  align-items: center;
  gap: ${({ gap }) => gap || 0};
`;

const Tag = styled.span<{
  isFromCV?: boolean;
  isRequired?: boolean;
  isWarning?: boolean;
}>`
  background-color: ${({ isRequired, isFromCV, isWarning }) =>
    isRequired
      ? palette.extended.red.shade100
      : isWarning
      ? palette.extended.red.shade100
      : isFromCV
      ? "#e0e0ff"
      : palette.beige.shade200};
  color: ${({ isRequired, isFromCV, isWarning }) =>
    isRequired
      ? palette.extended.red.shade800
      : isWarning
      ? palette.extended.red.shade800
      : isFromCV
      ? "#1f0e51"
      : palette.brand.black};
  border-radius: ${pxToRem(4)};
  padding: ${pxToRem(4)} ${pxToRem(8)};

  ${Text} {
    font-weight: 500;
  }
`;

const SubCategoryLabel = styled(Text).attrs({ size: -1 })`
  text-align: left;
  width: 160px;
  flex-shrink: 0;
  margin-bottom: ${pxToRem(4)};
`;

const EditButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
  cursor: pointer;
  border-radius: 50%;
  border: none;
  background: none;
  width: ${pxToRem(30)};
  height: ${pxToRem(30)};
  transition: default;

  &:hover {
    background: ${palette.brand.grey};
  }

  &:focus-visible {
    background: ${palette.brand.grey};
  }
`;

const MANDATORY_PREFERENCES = [
  "quality",
  "location",
  "function",
  "job-experience",
  "company-size",
];

export function PreferencesReview({
  setNextEnabled,
  setShowNegative,
}: QuestionComponentProps): React.ReactElement {
  const navigate = useNavigate();
  const hasAppliedDefaultRef = useRef(false);
  const { data, loading: loadingStatus } = useQuery(
    UserCvParsingStatusDocument
  );

  useEffect(() => {
    if (!loadingStatus && data?.currentUser?.cvParsingStatus !== "COMPLETED") {
      navigate("/initial-preferences/cv-upload");
    }
  }, [data, navigate, loadingStatus]);

  const { data: preferencesData, loading: loadingData } = useQuery(
    UserPreferencesReviewDocument,
    {
      fetchPolicy: "network-only",
    }
  );

  const { data: companySizesData } = useQuery(CompanySizesDocument);

  // If user already has preferences, mark as applied to avoid default logic
  useEffect(() => {
    if (
      !loadingData &&
      preferencesData?.currentUser?.companySizePreferences &&
      preferencesData.currentUser.companySizePreferences.length > 0
    ) {
      hasAppliedDefaultRef.current = true;
    }
  }, [loadingData, preferencesData]);

  const [updatePreferences, { loading: loadingPreferencesUpdate }] =
    useMutation(UpdateUserPreferencesDocument);

  // Apply default company size preferences (select all) on first load
  useEffect(() => {
    if (
      !loadingData &&
      !loadingPreferencesUpdate &&
      !hasAppliedDefaultRef.current &&
      preferencesData?.currentUser?.companySizePreferences?.length === 0 &&
      companySizesData?.companySizes
    ) {
      hasAppliedDefaultRef.current = true;

      updatePreferences({
        variables: {
          input: {
            companySizePreferences: companySizesData.companySizes.map(
              ({ id }) => ({
                companySizeId: id,
              })
            ),
          },
        },
      });
    }
  }, [
    loadingData,
    preferencesData,
    companySizesData,
    updatePreferences,
    loadingPreferencesUpdate,
  ]);

  // map user data to API JSON format
  const jobPreferencesReview = useMemo(
    () => formatUserToJobPreferencesReview(preferencesData?.currentUser),
    [preferencesData]
  );

  const [pathToEdit, setPathToEdit] = useState<string | null>(null);
  const toEdit = useMemo(() => {
    if (!pathToEdit) return undefined;
    const questions = standardQuizQuestions(true, true, true, true, true);
    return questions.find(q => q.path === pathToEdit);
  }, [pathToEdit]);

  const areMandatoryPreferencesCompleted = useMemo(() => {
    if (!jobPreferencesReview) return false;
    const allFields = Object.values(jobPreferencesReview).flatMap(
      category => category.fields
    );
    return MANDATORY_PREFERENCES.every(mandatoryPath => {
      if (mandatoryPath === "location") {
        // For location, check if EITHER location OR remote-location is valid
        const locationField = allFields.find(f => f.path === "location");
        const remoteLocationField = allFields.find(
          f => f.path === "remote-location"
        );

        const locationValid =
          locationField && !locationField.value.includes("Not selected");
        const remoteLocationValid =
          remoteLocationField &&
          !remoteLocationField.value.includes("Not selected");

        return locationValid || remoteLocationValid;
      }

      const field = allFields.find(f => f.path === mandatoryPath);
      if (!field) return false;

      if (mandatoryPath === "quality") {
        // Must have at least 3 company qualities selected
        const validQualities = field.value.filter(
          v => v !== "Not selected" && !v.startsWith("Select")
        );
        return validQualities.length >= 3;
      }

      return !field.value.includes("Not selected");
    });
  }, [jobPreferencesReview]);

  useEffect(() => {
    pushAnalyticsEvent({
      eventName: "Candidate Viewed Quiz Step: AI Preferences Review",
    });
    setNextEnabled?.(areMandatoryPreferencesCompleted);
    setShowNegative?.(false);
  }, [setNextEnabled, setShowNegative, areMandatoryPreferencesCompleted]);

  if (loadingStatus || loadingData) {
    return <Spinner />;
  }

  return (
    <Spacing size={3}>
      <Card>
        {jobPreferencesReview &&
          Object.entries(jobPreferencesReview).map(([key, data]) => {
            return (
              <Container key={key}>
                <Flex gap="4px">
                  <Icon icon={data.icon as OttaIconsId} />
                  <Text bold size={-1}>
                    {data.label}
                  </Text>
                </Flex>

                {data.fields.map(field => (
                  <>
                    <FieldRow key={field.name}>
                      <FieldContent>
                        <SubCategoryLabel size={-1}>
                          {field.label}
                        </SubCategoryLabel>
                        <Flex gap="8px" wrap="wrap">
                          {field.value.map(v => {
                            const isMandatory = MANDATORY_PREFERENCES.includes(
                              field.path
                            );
                            let isRequired =
                              v === "Not selected" && isMandatory;
                            // Special case for location: don't show "Required" if remote-location is filled
                            if (
                              field.path === "location" &&
                              v === "Not selected" &&
                              isMandatory
                            ) {
                              const allFields = Object.values(
                                jobPreferencesReview || {}
                              ).flatMap(category => category.fields);
                              const remoteLocationField = allFields.find(
                                f => f.path === "remote-location"
                              );
                              if (
                                remoteLocationField &&
                                !remoteLocationField.value.includes(
                                  "Not selected"
                                )
                              ) {
                                isRequired = false;
                              }
                            }
                            const isWarning = v.startsWith("Select");
                            const displayValue = isRequired ? "Required" : v;
                            return (
                              <Tag
                                key={v}
                                isFromCV={
                                  !isRequired &&
                                  !isWarning &&
                                  displayValue !== "Not selected"
                                }
                                isRequired={isRequired}
                                isWarning={isWarning}
                              >
                                <Text size={-1}>{displayValue}</Text>
                              </Tag>
                            );
                          })}
                        </Flex>
                      </FieldContent>
                      <EditButton
                        className="edit-btn"
                        onClick={() => {
                          pushAnalyticsEvent({
                            eventName: `Candidate Clicked Edit Preference Button: ${field?.path}`,
                          });
                          setPathToEdit(field?.path);
                        }}
                      >
                        <Icon size={0} icon="edit" />
                      </EditButton>
                    </FieldRow>
                    <Separator />
                  </>
                ))}
              </Container>
            );
          })}
      </Card>
      {!!toEdit && (
        <PreferencesReviewEditModal
          infos={toEdit}
          onClose={() => setPathToEdit(null)}
        />
      )}
    </Spacing>
  );
}
